import { defaultColumnsConfig } from './constants/defaultColumnsConfig';
import type { ColumnConfig } from './constants/defaultColumnsConfig';
import { validateCustomColumn } from './customColumns';
import type { CustomColumn, ColumnPart } from './customColumnTypes';
import type { Order } from '../../../types/apiResponse/order';

export const ORDER_COLUMNS_CONFIGURATION_FORMAT = 'smartsell-order-table';
export const ORDER_COLUMNS_CONFIGURATION_VERSION = 1;
const MAX_COLUMNS = 500;

const defaultLeafColumns = defaultColumnsConfig.filter((column) => column.subItems.length === 0);
const defaultColumnsByKey = new Map(defaultLeafColumns.map((column) => [column.key, column]));

export function mergeOrderColumnsWithDefaults(columns: ColumnConfig[]): ColumnConfig[] {
  // Retire the old source column from persisted preferences without changing other columns.
  const activeColumns = columns.filter((column) => column.key !== 'history_only');
  const keys = new Set(activeColumns.map((column) => column.key));
  return [
    ...activeColumns.map((column) => {
      const defaultColumn = defaultColumnsByKey.get(column.key);
      return defaultColumn ? { ...column, label: defaultColumn.label } : column;
    }),
    ...defaultLeafColumns.filter((column) => !keys.has(column.key)),
  ];
}

const normalizeLabel = (label: string) => label.normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

type OrderColumnsConfigurationFile = {
  format: typeof ORDER_COLUMNS_CONFIGURATION_FORMAT;
  version: typeof ORDER_COLUMNS_CONFIGURATION_VERSION;
  columns: ColumnConfig[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseCustomColumn(value: unknown): CustomColumn {
  if (!isRecord(value) || value.version !== 1
    || (value.type !== 'text' && value.type !== 'number')
    || !Array.isArray(value.parts) || value.parts.length > 200) {
    throw new Error('invalidOrderColumnsConfiguration');
  }

  const parts = value.parts.map((part): ColumnPart => {
    if (!isRecord(part)) throw new Error('invalidOrderColumnsConfiguration');
    if (part.type === 'text' && typeof part.value === 'string') {
      return { type: 'text', value: part.value };
    }
    if (part.type === 'field' && typeof part.path === 'string') {
      return { type: 'field', path: part.path };
    }
    throw new Error('invalidOrderColumnsConfiguration');
  });

  const definition: CustomColumn = { version: 1, type: value.type, parts };
  if (validateCustomColumn(definition)) throw new Error('invalidOrderColumnsConfiguration');
  return definition;
}

export function parseOrderColumnsConfiguration(value: unknown): ColumnConfig[] {
  if (!isRecord(value) || value.format !== ORDER_COLUMNS_CONFIGURATION_FORMAT
    || value.version !== ORDER_COLUMNS_CONFIGURATION_VERSION || !Array.isArray(value.columns)
    || value.columns.length > MAX_COLUMNS) {
    throw new Error('invalidOrderColumnsConfiguration');
  }

  const keys = new Set<string>();
  const labels = new Set(defaultLeafColumns.map((column) => normalizeLabel(column.label)));
  const entries = value.columns.filter((entry) => !isRecord(entry) || entry.key !== 'history_only');
  const columns = entries.map((entry): ColumnConfig => {
    if (!isRecord(entry) || typeof entry.key !== 'string'
      || typeof entry.fixed !== 'boolean' || typeof entry.visible !== 'boolean'
      || keys.has(entry.key)) {
      throw new Error('invalidOrderColumnsConfiguration');
    }

    const key = entry.key;
    keys.add(key);
    if (key.startsWith('custom:')) {
      if (!/^custom:[a-zA-Z0-9_-]{1,120}$/.test(key)
        || typeof entry.label !== 'string' || !entry.label.trim() || entry.label.length > 80) {
        throw new Error('invalidOrderColumnsConfiguration');
      }
      const normalizedLabel = normalizeLabel(entry.label);
      if (labels.has(normalizedLabel)) throw new Error('invalidOrderColumnsConfiguration');
      labels.add(normalizedLabel);
      return {
        key: key as `custom:${string}`,
        label: entry.label.trim(),
        fixed: entry.fixed,
        visible: entry.visible,
        subItems: [],
        custom: parseCustomColumn(entry.custom),
      };
    }

    const defaultColumn = defaultColumnsByKey.get(key as keyof Order);
    if (!defaultColumn || entry.custom !== undefined) {
      throw new Error('invalidOrderColumnsConfiguration');
    }
    return {
      ...defaultColumn,
      fixed: entry.fixed,
      visible: entry.visible,
      subItems: [],
    };
  });

  const missingDefaults = defaultLeafColumns
    .filter((column) => !keys.has(String(column.key)))
    .map((column) => ({ ...column, subItems: [] }));
  return [...columns, ...missingDefaults];
}

export function orderColumnsConfigurationFile(columns: ColumnConfig[]): OrderColumnsConfigurationFile {
  const parsedColumns = parseOrderColumnsConfiguration({
    format: ORDER_COLUMNS_CONFIGURATION_FORMAT,
    version: ORDER_COLUMNS_CONFIGURATION_VERSION,
    columns,
  });
  return {
    format: ORDER_COLUMNS_CONFIGURATION_FORMAT,
    version: ORDER_COLUMNS_CONFIGURATION_VERSION,
    columns: parsedColumns,
  };
}
